import React from 'react';
import { cn } from '@/lib/utils';

interface SkeletonProps {
  className?: string;
}

/**
 * Base Skeleton component with animated pulse effect.
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div 
      className={cn(
        'animate-pulse bg-gray-200 rounded',
        className
      )}
      aria-hidden="true"
    />
  );
}

/**
 * Skeleton for text lines.
 */
export function SkeletonText({ className, lines = 1 }: SkeletonProps & { lines?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          className={cn(
            'h-4',
            i === lines - 1 ? 'w-3/4' : 'w-full',
            className
          )} 
        />
      ))}
    </div>
  );
}

/**
 * Skeleton for a card container.
 */
export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div className={cn('bg-white border border-gray-200 rounded-lg p-4 space-y-3', className)}>
      <Skeleton className="h-5 w-1/3" />
      <SkeletonText lines={3} />
    </div>
  );
}

/**
 * Skeleton for a flow item in processing results.
 */
export function SkeletonFlow() {
  return (
    <div className="space-y-4">
      {/* Flow Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <Skeleton className="h-6 w-1/2 mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      
      {/* Phases */}
      <div className="space-y-4 pl-4 border-l-2 border-gray-100">
        {[1, 2].map((phaseNum) => (
          <div key={phaseNum} className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-3">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-5 w-32" />
            </div>
            
            {/* Steps */}
            <div className="space-y-3 pl-4 border-l-2 border-gray-100">
              {[1, 2].map((stepNum) => (
                <div key={stepNum} className="bg-blue-50 border border-gray-200 rounded-lg p-2.5">
                  <Skeleton className="h-4 w-40 mb-2" />
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <SkeletonText lines={2} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for processing results loading state.
 */
export function ProcessingResultsSkeleton() {
  return (
    <div className="space-y-6" aria-label="Loading processing results">
      <SkeletonFlow />
    </div>
  );
}

/**
 * Skeleton for document preview pane.
 */
export function DocumentPreviewSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <Skeleton className="h-5 w-32" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      </div>
      
      {/* Preview area */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Skeleton className="w-full max-w-md aspect-[3/4] rounded" />
      </div>
    </div>
  );
}

/**
 * Skeleton for the document page header.
 */
export function DocumentHeaderSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div>
            <Skeleton className="h-6 w-48 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-24 rounded" />
          <Skeleton className="h-9 w-24 rounded" />
        </div>
      </div>
    </div>
  );
}

/**
 * Full page skeleton for document view.
 */
export function DocumentViewSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 mt-16 space-y-6">
      <DocumentHeaderSkeleton />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DocumentPreviewSkeleton />
        <div className="bg-white rounded-lg shadow p-4">
          <div className="border-b border-gray-200 pb-3 mb-4">
            <Skeleton className="h-5 w-40" />
          </div>
          <ProcessingResultsSkeleton />
        </div>
      </div>
    </div>
  );
}

export default Skeleton;
